import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useInfiniteQuery } from '@tanstack/react-query';

import { fetchData as defaultFetchData } from 'api/FetchData';

import { iaRa } from 'lib/helpers/utility';

const getByPath = (obj, path) => path.split('.').reduce((acc, key) => acc?.[key], obj);

const flattenInfiniteQueryPages = ({
  pages,
  queryKey,
  formatData = (data) => data,
  isOptions = false
}) => {
  const safePages = iaRa(pages);

  if (isOptions) {
    return safePages.flatMap((page) => page?.options);
  }

  let result = safePages.flatMap((page) => getByPath(page, queryKey));

  const isPathFound = safePages.some((page) => {
    const value = getByPath(page, queryKey);
    return value !== undefined && value !== null;
  });

  if (!isPathFound) {
    result = safePages;
  }

  return formatData(result);
};

export const useInfiniteFetchData = ({
  queryKey,
  resultKey = queryKey,
  url,
  params = {},
  options = {},
  dependencies = [],
  onSuccess = () => {},
  formatData = (data) => data,
  queryFn,
  isOptions = false
}) => {
  const navigate = useNavigate();
  const paramsRef = useRef(params);

  // Update params ref only when params actually change
  useEffect(() => {
    paramsRef.current = params || {};
  }, [JSON.stringify(params || {})]);

  if (!queryKey) {
    throw new Error('The "queryKey" parameter is required for useInfiniteFetchData.');
  }

  if (!queryFn && !url) {
    throw new Error('Either "fetchData" or "url" parameter is required for useInfiniteFetchData.');
  }

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    refetch: refetchQueryFn
  } = useInfiniteQuery(
    [queryKey, ...dependencies],
    ({ pageParam = paramsRef.current }) => {
      if (queryFn) {
        return queryFn(navigate, pageParam);
      }
      return defaultFetchData(url, navigate, pageParam);
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        const count = Number(lastPage?.count || 0);
        const totalFetched = allPages.reduce(
          (acc, page) =>
            acc +
            (isOptions ? page?.options?.length || 0 : getByPath(page, resultKey)?.length || 0),
          0
        );

        return totalFetched < count
          ? { ...paramsRef.current, page: allPages.length + 1 }
          : undefined;
      },
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      ...options,
      onSuccess
    }
  );

  // Helper function to reset params ref to original params if needed
  const resetRefParams = useCallback(() => {
    paramsRef.current = params;
  }, [params]);

  // Create a wrapper refetch function that accepts new params
  const refetch = useCallback(
    async ({ params = null, options = {} } = {}) => {
      const hasNewParams = params && Object.keys(params || {}).length > 0;

      if (!hasNewParams) {
        // If no newParams passed, behave like the original refetch
        return refetchQueryFn(options);
      }

      // If new params are provided and not empty, merge with existing params
      paramsRef.current = { ...paramsRef.current, ...params };

      // Just refetch with updated params - no need to invalidate first
      const responseResult = await refetchQueryFn(options);

      const pages = responseResult?.data?.pages;

      if (!pages) return [];

      return flattenInfiniteQueryPages({
        pages,
        queryKey,
        formatData,
        isOptions
      });
    },
    [refetchQueryFn, queryKey, formatData, isOptions]
  );

  const flattenedData = useMemo(() => {
    return flattenInfiniteQueryPages({
      pages: data?.pages,
      queryKey,
      formatData,
      isOptions
    });
  }, [data, queryKey, formatData, isOptions]);

  return {
    data: flattenedData,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isLoading,
    isFetchingNextPage,
    refetch,
    resetRefParams
  };
};
